import { useEffect, useState, useMemo, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import EventIcon from '@mui/icons-material/Event';
import RotateRightIcon from '@mui/icons-material/RotateRight';

import api from '~/services/api';
import { useStoreActions } from '~/store/hooks';
import {
  formatToIsoWithoutTimezone,
  getLastWeekDateRange,
  getLastMonthDateRange,
} from '~/utils/date';
import downloadFile from '~/utils/downloadFile';
import { getFileNameFromContentDisposition } from '~/utils/headers';
import { extractErrorMessage } from '~/utils/error';
import { getInitialValues, validate } from './form';
import Button from '~/ui/components/common/Button';
import SelectVirtualized from '~/ui/components/inputs/SelectVirtualized';
import SelectAll from '~/ui/components/inputs/SelectAll';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Checkbox from '~/ui/components/inputs/Checkbox';
import RadioGroup from '~/ui/components/inputs/RadioGroup';

import { IPatientSummaryReportRequest } from '~/services/api/reports/types';
import { IFormValues } from './types';
import { DateRangeType } from '../types';
import styles from './PatientSummary.module.scss';
import { IIdName } from '~/services/api/types';
import readingsModeOptions from '~/ui/constants/readingsModeOptions';
import { PatientSelectorType } from './enums';
import { selectorTypeOptions } from './constants';

const PatientSummary = (): ReactElement => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [programs, setPrograms] = useState<IIdName[]>([]);
  const [healthManagers, setHealthManagers] = useState<IIdName[]>([]);
  const [patients, setPatients] = useState<IIdName[]>([]);
  const [isSelectorsLoading, setIsSelectorsLoading] = useState(false);
  const [isPatientsLoading, setIsPatientsLoading] = useState(false);

  const showError = useStoreActions(actions => actions.snackbar.showError);

  const initialValues = getInitialValues();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: validate,
    defaultValues: initialValues,
  });

  const selectorType = watch('selectorType');
  const programId = watch('programId');
  const healthManagerId = watch('healthManagerId');
  const includeReadings = watch('includeReadings');
  const isByProgram = selectorType === PatientSelectorType.Program;
  const selectedSelectorId = isByProgram ? programId : healthManagerId;

  const programOptions = useMemo(
    () =>
      programs.map(item => ({
        value: item.id,
        label: item.name,
      })),
    [programs],
  );

  const healthManagerOptions = useMemo(
    () =>
      healthManagers.map(item => ({
        value: item.id,
        label: item.name,
      })),
    [healthManagers],
  );

  const patientOptions = patients.map(item => ({
    value: item.id,
    label: item.name,
  }));

  const getDropdownOptions = async () => {
    if (!selectedSelectorId) {
      setPatients([]);
      return;
    }

    try {
      setIsPatientsLoading(true);
      const patientsList = isByProgram
        ? await api.reports
            .getPatientsByProgramId({ programId: selectedSelectorId })
            .then(res => res.data)
        : await api.reports
            .getPatientsByHealthManagerId({ healthManagerId: selectedSelectorId })
            .then(res => res.data);

      setPatients(patientsList);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsPatientsLoading(false);
    }
  };

  const generateReport = async (payload: IPatientSummaryReportRequest) => {
    try {
      setIsProcessing(true);
      const { data, headers } = await api.reports.getPatientSummaryReport(payload);
      const fileName = getFileNameFromContentDisposition(headers['content-disposition']);
      downloadFile(window.URL.createObjectURL(new Blob([data])), fileName);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsProcessing(false);
    }
  };

  const onSubmitForm = (values: IFormValues) => {
    const payload = { ...values };
    delete payload.selectorType;
    delete payload.programId;
    delete payload.healthManagerId;

    generateReport({
      ...payload,
      startDate: formatToIsoWithoutTimezone(payload.startDate),
      endDate: formatToIsoWithoutTimezone(payload.endDate),
    });
  };

  const resetDateRange = (type: DateRangeType) => {
    let dateRange = getLastWeekDateRange();

    if (type === DateRangeType.Month) {
      dateRange = getLastMonthDateRange();
    }

    setValue('startDate', dateRange.startDate);
    setValue('endDate', dateRange.endDate);
  };

  const onMount = async () => {
    try {
      setIsSelectorsLoading(true);
      const selectors = await api.reports.getSelectors().then(res => res.data);
      setPrograms(selectors.programs);
      setHealthManagers(selectors.healthManagers);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsSelectorsLoading(false);
    }
  };

  useEffect(() => {
    if (isByProgram) {
      setValue('healthManagerId', null);
    } else {
      setValue('programId', null);
    }
  }, [isByProgram]);

  useEffect(() => {
    getDropdownOptions();
    setValue('patientIds', []);
  }, [selectorType, programId, healthManagerId]);

  useEffect(() => {
    onMount();
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <div className={styles.title}>Patient Summary</div>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <RadioGroup
            name="selectorType"
            control={control}
            errors={errors}
            options={selectorTypeOptions}
            label="Select Patients By"
            required
          />
        </Grid>
        <Grid item xs={6} />
        <Grid item xs={6}>
          {isByProgram ? (
            <SelectVirtualized
              name="programId"
              control={control}
              errors={errors}
              options={programOptions}
              isLoading={isSelectorsLoading}
              label="Program Name"
              required
            />
          ) : (
            <SelectVirtualized
              name="healthManagerId"
              control={control}
              errors={errors}
              options={healthManagerOptions}
              isLoading={isSelectorsLoading}
              label="Health Manager"
              required
            />
          )}
        </Grid>
        <Grid item xs={6} />
        <Grid item xs={6}>
          <SelectAll
            name="patientIds"
            control={control}
            errors={errors}
            options={patientOptions}
            isLoading={isPatientsLoading}
            label="Patient Name"
            required
          />
        </Grid>
        <Grid item xs={6} />
        <Grid item className={styles.blockTitle} xs={12}>
          Adjustments
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={2}>
              <Checkbox
                name="includeCharts"
                control={control}
                errors={errors}
                label="Readings Graph"
              />
            </Grid>
            <Grid item xs={6}>
              <Checkbox name="includeNotes" control={control} errors={errors} label="Notes" />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={2}>
              <Checkbox name="includeReadings" control={control} errors={errors} label="Readings" />
            </Grid>
            {includeReadings && (
              <Grid item xs={6}>
                <RadioGroup
                  name="readingsMode"
                  control={control}
                  errors={errors}
                  options={readingsModeOptions}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            placeholder=".. /.. /.."
            label="Start Date"
            name="startDate"
            maxDate={new Date()}
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            placeholder=".. /.. /.."
            label="End Date"
            name="endDate"
            maxDate={new Date()}
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            startIcon={<EventIcon />}
            onClick={() => resetDateRange(DateRangeType.Week)}
          >
            Last Week
          </Button>
          <div className={styles.spacer} />
          <Button
            type="button"
            variant="outlined"
            color="primary"
            startIcon={<EventIcon />}
            onClick={() => resetDateRange(DateRangeType.Month)}
          >
            Last Month
          </Button>
        </Grid>
        <Grid item xs={6} className={styles.rightBtnCol}>
          <Button
            type="button"
            variant="outlined"
            startIcon={<RotateRightIcon />}
            onClick={() => {
              reset(initialValues);
            }}
          >
            Reset
          </Button>
          <div className={styles.spacer} />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            isLoading={isProcessing}
            disabled={isProcessing}
          >
            Generate
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default PatientSummary;
